/**
 * SlashCommandPicker — inline "/" slash command picker for composer inputs.
 *
 * Triggered when the user types "/" at the start of a line or after whitespace.
 * Displays available slash commands with descriptions, supports keyboard
 * navigation (↑/↓/Enter/Tab/Escape), and calls onSelect(command) on pick.
 *
 * Visual treatment matches MentionPicker so both pickers feel identical.
 * Exports parseSlashAt() for use by host components.
 */

import { useEffect, useMemo, useState } from "react";

export interface SlashCommandDef {
  command: string;
  description: string;
}

export const SLASH_COMMANDS: SlashCommandDef[] = [
  { command: "/sprint-status", description: "Current sprint summary and track status" },
  { command: "/who-is-working", description: "Which agents are busy and what they're on" },
  { command: "/help", description: "Ask @relay a question (adds 'help' tag)" },
  { command: "/blocker", description: "Report a blocker requiring Travis action" },
  { command: "/decision", description: "Log a team decision for the record" },
  { command: "/pre-flight", description: "Request approval before a MEDIUM+ blast change" },
  { command: "/status", description: "Post an agent status check-in" },
];

/**
 * Parse a slash command trigger at the current cursor position.
 * Returns { start, query } when "/" is found at the start of text
 * or after whitespace, immediately before the cursor.
 */
export function parseSlashAt(
  text: string,
  cursor: number,
): { start: number; query: string } | null {
  const before = text.slice(0, cursor);
  const match = before.match(/(?:^|\s)(\/([a-z-]*))$/);
  if (!match) return null;
  const slashIndex = before.lastIndexOf("/");
  return { start: slashIndex, query: match[2] };
}

interface Props {
  query: string;
  onSelect: (command: string) => void;
  onClose: () => void;
}

export function SlashCommandPicker({ query, onSelect, onClose }: Props) {
  const filtered = useMemo(
    () => SLASH_COMMANDS.filter((c) => c.command.slice(1).startsWith(query.toLowerCase())),
    [query],
  );
  const [highlightIdx, setHighlightIdx] = useState(0);

  useEffect(() => {
    setHighlightIdx(0);
  }, [filtered.length]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (filtered.length === 0 && e.key !== "Escape") return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setHighlightIdx((i) => (i + 1) % filtered.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setHighlightIdx((i) => (i - 1 + filtered.length) % filtered.length);
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        const pick = filtered[highlightIdx];
        if (pick) onSelect(pick.command);
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [filtered, highlightIdx, onSelect, onClose]);

  if (filtered.length === 0) return null;

  return (
    <div
      role="listbox"
      aria-label="Slash commands"
      style={{
        position: "absolute",
        bottom: "calc(100% + 4px)",
        left: 0,
        zIndex: 11,
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 6,
        padding: 4,
        minWidth: 280,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      {filtered.map((cmd, i) => {
        const isActive = i === highlightIdx;
        return (
          <button
            key={cmd.command}
            type="button"
            role="option"
            aria-selected={isActive}
            onMouseEnter={() => setHighlightIdx(i)}
            onClick={() => onSelect(cmd.command)}
            style={{
              display: "flex",
              width: "100%",
              textAlign: "left",
              background: isActive ? "var(--surface2)" : "transparent",
              color: "var(--text)",
              border: "none",
              padding: "6px 10px",
              fontSize: 12,
              fontFamily: "var(--font-mono)",
              cursor: "pointer",
              borderRadius: 4,
              gap: 10,
              alignItems: "baseline",
            }}
          >
            <span style={{ fontWeight: 600, color: "var(--accent)", flexShrink: 0 }}>
              {cmd.command}
            </span>
            <span style={{ color: "var(--text2)", fontSize: 11 }}>{cmd.description}</span>
          </button>
        );
      })}
    </div>
  );
}
